/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, ReactNode, useCallback, useRef, useEffect, useMemo } from 'react';

interface PlaylistModalContextType {
  playlistId: string | null;
  isOpen: boolean;
  openModal: (playlistId: string) => void;
  closeModal: () => void;
}

const PlaylistModalContext = createContext<PlaylistModalContextType | undefined>(undefined);

export const usePlaylistModal = () => {
  const context = useContext(PlaylistModalContext);
  if (!context) {
    throw new Error('usePlaylistModal must be used within PlaylistModalProvider');
  }
  return context;
};

interface PlaylistModalProviderProps {
  children: ReactNode;
}

export const PlaylistModalProvider = ({ children }: PlaylistModalProviderProps) => {
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const openModal = useCallback((id: string) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setPlaylistId(id);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Delay clearing playlistId to allow exit animations
    closeTimerRef.current = setTimeout(() => setPlaylistId(null), 300);
  }, []);

  const value = useMemo(() => ({
    playlistId,
    isOpen,
    openModal,
    closeModal,
  }), [playlistId, isOpen, openModal, closeModal]);

  return (
    <PlaylistModalContext.Provider value={value}>
      {children}
    </PlaylistModalContext.Provider>
  );
};

